import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {MainCampaign} from 'utils/multiCampaignTypes';
import ApiService from 'redux/services/api-service';

export interface FilterOptions {
  clients: Array<{id: number; name: string}>;
  brands: Array<{id: number; name: string}>;
  retailers: Array<{id: number; name: string}>;
}

export interface MultiCampaignState {
  mainCampaigns: MainCampaign[];
  mainCampaignsLoading: boolean;
  mainCampaignsError: object | null;
  filterOptions: FilterOptions | null;
  filterOptionsLoading: boolean;
  filterOptionsError: object | null;
  weeklySalesLoading: boolean;
  weeklySalesError: object | null;
  campaignItemsLoading: boolean;
  campaignItemsError: object | null;
  campaignStoresLoading: boolean;
  campaignStoresError: object | null;
  campaignAreasLoading: boolean;
  campaignAreasError: object | null;
  attributeOptionsLoading: boolean;
  attributeOptionsError: object | null;
  attributeWeeklyLoading: boolean;
  attributeWeeklyError: object | null;
  locationAttributeOptionsLoading: boolean;
  locationAttributeOptionsError: object | null;
  locationAttributeWeeklyLoading: boolean;
  locationAttributeWeeklyError: object | null;
}

export const multiCampaignEndpoints = {
  getMainCampaigns: createAsyncThunk('multicampaign/getMainCampaigns', async (params: any) => {
    const response = await ApiService.get(`/multi-campaign/main_campaigns/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getFilterOptions: createAsyncThunk('multicampaign/getFilterOptions', async () => {
    const response = await ApiService.get('/multi-campaign/filter-options/');
    return response.data;
  }),
  getWeeklySalesData: createAsyncThunk('multicampaign/getWeeklySalesData', async (pathIds: string[]) => {
    const pathIdsParam = pathIds.join(',');
    const response = await ApiService.get(`/multi-campaign/weekly-sales/?path_ids=${pathIdsParam}`);
    return response.data;
  }),
  getCampaignItemsData: createAsyncThunk('multicampaign/getCampaignItemsData', async (pathId: string) => {
    const response = await ApiService.get(`/multi-campaign/campaign-items/?path_ids=${pathId}`);
    return response.data;
  }),
  getCampaignStoresData: createAsyncThunk('multicampaign/getCampaignStoresData', async (pathId: string) => {
    const response = await ApiService.get(`/multi-campaign/campaign-stores/?path_ids=${pathId}`);
    return response.data;
  }),
  getCampaignAreasData: createAsyncThunk('multicampaign/getCampaignAreasData', async (pathId: string) => {
    const response = await ApiService.get(`/multi-campaign/campaign-areas/?path_ids=${pathId}`);
    return response.data;
  }),
  getAttributeOptions: createAsyncThunk('multicampaign/getAttributeOptions', async (pathId: string) => {
    const response = await ApiService.get(`/multi-campaign/attribute-options/?path_id=${pathId}&type=item`);
    return {pathId, results: response.data.results as string[]};
  }),
  getAttributeWeeklyComparison: createAsyncThunk(
    'multicampaign/getAttributeWeeklyComparison',
    async (args: {pathId: string; attribute: string; metric: string; topN?: number}) => {
      const {pathId, attribute, metric, topN} = args;
      const params = new URLSearchParams({path_id: pathId, type: 'item', attribute, metric});
      if (topN) params.set('top_n', String(topN));
      const response = await ApiService.get(`/multi-campaign/weekly-attribute-comparison/?${params.toString()}`);
      return {pathId, attribute, metric, results: response.data.results};
    },
  ),
  getLocationAttributeOptions: createAsyncThunk('multicampaign/getLocationAttributeOptions', async (pathId: string) => {
    const response = await ApiService.get(`/multi-campaign/location-attribute-options/?path_id=${pathId}`);
    return {
      pathId,
      results: response.data.results as Array<{value: string; label: string}>,
      regional: response.data.regional,
    };
  }),
  getLocationAttributeWeeklyComparison: createAsyncThunk(
    'multicampaign/getLocationAttributeWeeklyComparison',
    async (args: {pathId: string; attribute: string; metric: string; topN?: number}) => {
      const {pathId, attribute, metric, topN} = args;
      const params = new URLSearchParams({path_id: pathId, attribute, metric});
      if (topN) params.set('top_n', String(topN));
      const response = await ApiService.get(
        `/multi-campaign/weekly-location-attribute-comparison/?${params.toString()}`,
      );
      return {pathId, attribute, metric, results: response.data.results};
    },
  ),
  exportMultiCampaignReport: createAsyncThunk('multicampaign/exportReport', async (campaignIds: number[]) => {
    const response = await ApiService.post<Blob>(
      '/multi-campaign/export-report/',
      {campaign_ids: campaignIds},
      {responseType: 'blob', timeout: 120_000},
    );
    return response.data;
  }),
};
export const multiCampaignSlice = createSlice({
  name: 'multicampaign',
  initialState: {
    mainCampaigns: [],
    mainCampaignsLoading: false,
    mainCampaignsError: null,
    filterOptions: null,
    filterOptionsLoading: false,
    filterOptionsError: null,
    weeklySalesLoading: false,
    weeklySalesError: null,
    campaignItemsLoading: false,
    campaignItemsError: null,
    campaignStoresLoading: false,
    campaignStoresError: null,
    campaignAreasLoading: false,
    campaignAreasError: null,
    attributeOptionsLoading: false,
    attributeOptionsError: null,
    attributeWeeklyLoading: false,
    attributeWeeklyError: null,
    locationAttributeOptionsLoading: false,
    locationAttributeOptionsError: null,
    locationAttributeWeeklyLoading: false,
    locationAttributeWeeklyError: null,
  } as MultiCampaignState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(multiCampaignEndpoints.getMainCampaigns.pending, state => {
      state.mainCampaignsLoading = true;
      state.mainCampaignsError = null;
      state.mainCampaigns = [];
    });
    builder.addCase(multiCampaignEndpoints.getMainCampaigns.fulfilled, (state, {payload}) => {
      state.mainCampaignsLoading = false;
      state.mainCampaignsError = null;
      state.mainCampaigns = payload.results;
    });
    builder.addCase(multiCampaignEndpoints.getMainCampaigns.rejected, (state, {payload}) => {
      state.mainCampaignsLoading = false;
      state.mainCampaignsError = payload as object;
      state.mainCampaigns = [];
    });

    builder.addCase(multiCampaignEndpoints.getFilterOptions.pending, state => {
      state.filterOptionsLoading = true;
      state.filterOptionsError = null;
    });
    builder.addCase(multiCampaignEndpoints.getFilterOptions.fulfilled, (state, {payload}) => {
      state.filterOptionsLoading = false;
      state.filterOptionsError = null;
      state.filterOptions = payload;
    });
    builder.addCase(multiCampaignEndpoints.getFilterOptions.rejected, (state, {payload}) => {
      state.filterOptionsLoading = false;
      state.filterOptionsError = payload as object;
    });

    // Weekly Sales Data cases
    builder.addCase(multiCampaignEndpoints.getWeeklySalesData.pending, state => {
      state.weeklySalesLoading = true;
      state.weeklySalesError = null;
    });
    builder.addCase(multiCampaignEndpoints.getWeeklySalesData.fulfilled, (state, {payload}) => {
      state.weeklySalesLoading = false;
      state.weeklySalesError = null;

      // Update the mainCampaigns with weekly sales data
      const weeklySalesData = payload.results;

      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(pathId => {
          // Check if we have weekly sales data for this pathId
          if (weeklySalesData[pathId.pathId]) {
            return {
              ...pathId,
              weeklySalesData: weeklySalesData[pathId.pathId],
            };
          }
          return pathId;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getWeeklySalesData.rejected, (state, {payload}) => {
      state.weeklySalesLoading = false;
      state.weeklySalesError = payload as object;
    });

    // Campaign Items Data cases
    builder.addCase(multiCampaignEndpoints.getCampaignItemsData.pending, state => {
      state.campaignItemsLoading = true;
      state.campaignItemsError = null;
    });
    builder.addCase(multiCampaignEndpoints.getCampaignItemsData.fulfilled, (state, {payload}) => {
      state.campaignItemsLoading = false;
      state.campaignItemsError = null;

      // Update the mainCampaigns with campaign items data for a single pathId
      const campaignItemsData = payload.results;

      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(pathId => {
          // Check if we have items data for this specific pathId
          if (campaignItemsData[pathId.pathId]) {
            return {
              ...pathId,
              items: campaignItemsData[pathId.pathId],
            };
          }
          return pathId;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getCampaignItemsData.rejected, (state, {payload}) => {
      state.campaignItemsLoading = false;
      state.campaignItemsError = payload as object;
    });

    // Campaign Stores Data cases
    builder.addCase(multiCampaignEndpoints.getCampaignStoresData.pending, state => {
      state.campaignStoresLoading = true;
      state.campaignStoresError = null;
    });
    builder.addCase(multiCampaignEndpoints.getCampaignStoresData.fulfilled, (state, {payload}) => {
      state.campaignStoresLoading = false;
      state.campaignStoresError = null;

      // Update the mainCampaigns with campaign stores data for a single pathId
      const campaignStoresData = payload.results;

      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(pathId => {
          // Check if we have stores data for this specific pathId
          if (campaignStoresData[pathId.pathId]) {
            return {
              ...pathId,
              stores: campaignStoresData[pathId.pathId],
            };
          }
          return pathId;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getCampaignStoresData.rejected, (state, {payload}) => {
      state.campaignStoresLoading = false;
      state.campaignStoresError = payload as object;
    });

    // Campaign Areas Data cases
    builder.addCase(multiCampaignEndpoints.getCampaignAreasData.pending, state => {
      state.campaignAreasLoading = true;
      state.campaignAreasError = null;
    });
    builder.addCase(multiCampaignEndpoints.getCampaignAreasData.fulfilled, (state, {payload}) => {
      state.campaignAreasLoading = false;
      state.campaignAreasError = null;

      // Update the mainCampaigns with campaign areas data for a single pathId
      const campaignAreasData = payload.results;

      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(pathId => {
          // Check if we have areas data for this specific pathId
          if (campaignAreasData[pathId.pathId]) {
            return {
              ...pathId,
              regions: campaignAreasData[pathId.pathId],
            };
          }
          return pathId;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getCampaignAreasData.rejected, (state, {payload}) => {
      state.campaignAreasLoading = false;
      state.campaignAreasError = payload as object;
    });

    // Attribute Options
    builder.addCase(multiCampaignEndpoints.getAttributeOptions.pending, state => {
      state.attributeOptionsLoading = true;
      state.attributeOptionsError = null;
    });
    builder.addCase(multiCampaignEndpoints.getAttributeOptions.fulfilled, (state, {payload}) => {
      state.attributeOptionsLoading = false;
      state.attributeOptionsError = null;
      const {pathId, results} = payload as {pathId: string; results: string[]};
      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(path => {
          if (path.pathId === pathId) {
            return {
              ...path,
              attributeOptions: results,
            };
          }
          return path;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getAttributeOptions.rejected, (state, {payload}) => {
      state.attributeOptionsLoading = false;
      state.attributeOptionsError = payload as object;
    });

    // Attribute Weekly Comparison
    builder.addCase(multiCampaignEndpoints.getAttributeWeeklyComparison.pending, state => {
      state.attributeWeeklyLoading = true;
      state.attributeWeeklyError = null;
    });
    builder.addCase(multiCampaignEndpoints.getAttributeWeeklyComparison.fulfilled, (state, {payload}) => {
      state.attributeWeeklyLoading = false;
      state.attributeWeeklyError = null;
      const {pathId, attribute, metric, results} = payload as {
        pathId: string;
        attribute: string;
        metric: string;
        results: {
          series: Array<{
            group: string;
            data: Array<{weekStartDate: string; value: number; baseline?: number}>;
          }>;
        };
      };
      const key = `${attribute}|${metric}`;
      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(path => {
          if (path.pathId === pathId) {
            const existing = path.attributeSeries || {};
            return {
              ...path,
              attributeSeries: {
                ...existing,
                [key]: results.series,
              },
            };
          }
          return path;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getAttributeWeeklyComparison.rejected, (state, {payload}) => {
      state.attributeWeeklyLoading = false;
      state.attributeWeeklyError = payload as object;
    });

    // Location Attribute Options
    builder.addCase(multiCampaignEndpoints.getLocationAttributeOptions.pending, state => {
      state.locationAttributeOptionsLoading = true;
      state.locationAttributeOptionsError = null;
    });
    builder.addCase(multiCampaignEndpoints.getLocationAttributeOptions.fulfilled, (state, {payload}) => {
      state.locationAttributeOptionsLoading = false;
      state.locationAttributeOptionsError = null;
      const {pathId, results, regional} = payload as {
        pathId: string;
        results: Array<{value: string; label: string}>;
        regional: boolean;
      };
      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(path => {
          if (path.pathId === pathId) {
            return {
              ...path,
              locationAttributeOptions: results,
              regional,
            };
          }
          return path;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getLocationAttributeOptions.rejected, (state, {payload}) => {
      state.locationAttributeOptionsLoading = false;
      state.locationAttributeOptionsError = payload as object;
    });

    // Location Attribute Weekly Comparison
    builder.addCase(multiCampaignEndpoints.getLocationAttributeWeeklyComparison.pending, state => {
      state.locationAttributeWeeklyLoading = true;
      state.locationAttributeWeeklyError = null;
    });
    builder.addCase(multiCampaignEndpoints.getLocationAttributeWeeklyComparison.fulfilled, (state, {payload}) => {
      state.locationAttributeWeeklyLoading = false;
      state.locationAttributeWeeklyError = null;
      const {pathId, attribute, metric, results} = payload as {
        pathId: string;
        attribute: string;
        metric: string;
        results: {
          series: Array<{
            group: string;
            data: Array<{weekStartDate: string; value: number; baseline?: number}>;
          }>;
        };
      };
      const key = `${attribute}|${metric}`;
      state.mainCampaigns = state.mainCampaigns.map(campaign => ({
        ...campaign,
        pathIds: campaign.pathIds.map(path => {
          if (path.pathId === pathId) {
            const existing = path.locationAttributeSeries || {};
            return {
              ...path,
              locationAttributeSeries: {
                ...existing,
                [key]: results.series,
              },
            };
          }
          return path;
        }),
      }));
    });
    builder.addCase(multiCampaignEndpoints.getLocationAttributeWeeklyComparison.rejected, (state, {payload}) => {
      state.locationAttributeWeeklyLoading = false;
      state.locationAttributeWeeklyError = payload as object;
    });
  },
});

export const multiCampaignActions = multiCampaignSlice.actions;

export type MultiCampaignSlice = {
  [multiCampaignSlice.name]: ReturnType<(typeof multiCampaignSlice)['reducer']>;
};
